// src/components/ROICalculator.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Box, 
  Typography, 
  TextField, 
  Slider, 
  Grid,
  Button,
  InputAdornment,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Modern styled section wrapper
const Section = ({ title, tooltip, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 700,
            color: '#231f20',
            letterSpacing: '0.02em',
            fontFamily: '"Outfit", sans-serif'
          }}
        >
          {title}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 18, color: '#8a9aa8', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      {children}
    </Box>
  </motion.div>
);

// Modern toggle button group
const ToggleButtonGroup = ({ options, value, onChange, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Box sx={{ 
      display: 'flex', 
      gap: 1,
      p: 0.5,
      bgcolor: 'rgba(0,0,0,0.03)',
      borderRadius: 2,
      flexWrap: 'wrap'
    }}>
      {options.map((option) => (
        <motion.div 
          key={option.value}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{ flex: 1, minWidth: '80px' }}
        >
          <Button
            onClick={() => onChange(option.value)}
            sx={{ 
              width: '100%',
              py: 1.5,
              borderRadius: 1.5,
              fontWeight: 600,
              fontSize: '0.85rem',
              transition: 'all 0.3s ease',
              bgcolor: value === option.value 
                ? 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)' 
                : 'transparent',
              background: value === option.value 
                ? 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)' 
                : 'transparent',
              color: value === option.value ? 'white' : '#5a6775',
              boxShadow: value === option.value 
                ? '0 4px 15px rgba(244, 114, 22, 0.3)' 
                : 'none',
              '&:hover': {
                bgcolor: value === option.value 
                  ? 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)'
                  : 'rgba(244, 114, 22, 0.08)',
                background: value === option.value 
                  ? 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)'
                  : 'rgba(244, 114, 22, 0.08)',
              }
            }}
          >
            {option.label}
          </Button>
        </motion.div>
      ))}
    </Box>
  </motion.div>
);

// Modern slider with value display
const ModernSlider = ({ value, onChange, min, max, marks, suffix = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <Box sx={{ flexGrow: 1 }}>
        <Slider
          value={value}
          onChange={(e, newValue) => onChange(newValue)}
          min={min}
          max={max}
          marks={marks}
          sx={{
            height: 10,
            '& .MuiSlider-track': {
              border: 'none',
              background: 'linear-gradient(90deg, #f47216 0%, #db1d2b 100%)',
            },
            '& .MuiSlider-rail': {
              bgcolor: 'rgba(0,0,0,0.08)',
            },
            '& .MuiSlider-thumb': {
              width: 26,
              height: 26,
              backgroundColor: '#fff',
              border: '3px solid #f47216',
              boxShadow: '0 4px 15px rgba(244, 114, 22, 0.3)',
              '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                boxShadow: '0 6px 20px rgba(244, 114, 22, 0.4)',
              },
              '&::before': {
                display: 'none',
              },
            },
            '& .MuiSlider-markLabel': {
              fontSize: '0.75rem',
              color: '#8a9aa8',
              fontWeight: 500,
            },
          }}
        />
      </Box>
      <Box sx={{ 
        minWidth: '80px',
        textAlign: 'center',
        py: 1.5,
        px: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(244, 114, 22, 0.1) 0%, rgba(219, 29, 43, 0.1) 100%)',
        border: '1px solid rgba(244, 114, 22, 0.2)'
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            color: '#f47216',
            fontFamily: '"JetBrains Mono", monospace'
          }}
        >
          {value}{suffix}
        </Typography>
      </Box>
    </Box>
  </motion.div>
);

// Complexity button
const ComplexityButton = ({ value, currentValue, onClick, label, color, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, delay }}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    style={{ flex: '1 1 0', minWidth: '100px' }}
  >
    <Button
      onClick={onClick}
      sx={{ 
        width: '100%',
        py: 1.5,
        px: 1,
        borderRadius: 3,
        fontWeight: 600,
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        transition: 'all 0.3s ease',
        bgcolor: currentValue === value ? color : 'transparent',
        color: currentValue === value ? 'white' : color,
        border: `2px solid ${color}`,
        boxShadow: currentValue === value 
          ? `0 4px 15px ${color}40` 
          : 'none',
        '&:hover': {
          bgcolor: currentValue === value ? color : `${color}15`,
          borderColor: color,
        }
      }}
    >
      {label}
    </Button>
  </motion.div>
);

function ROICalculator({ onCalculate }) {
  // State variables
  const [hourlyLaborCost, setHourlyLaborCost] = useState(35);
  const [processTime, setProcessTime] = useState(30);
  const [frequency, setFrequency] = useState('daily');
  const [timesPerDay, setTimesPerDay] = useState(10);
  const [workingDaysPerWeek, setWorkingDaysPerWeek] = useState(5);
  const [errorRateBefore, setErrorRateBefore] = useState(15);
  const [rpaType, setRpaType] = useState('uipath');
  const [complexity, setComplexity] = useState('veryComplex');

  // Calculate results whenever inputs change
  useEffect(() => {
    calculateROI();
  }, [hourlyLaborCost, processTime, frequency, timesPerDay, workingDaysPerWeek, errorRateBefore, rpaType, complexity]);

  const calculateROI = () => {
    // Calculate annual volume
    const annualVolume = frequency === 'daily' 
      ? timesPerDay * workingDaysPerWeek * 52 
      : frequency === 'weekly' 
        ? workingDaysPerWeek * 52 
        : 12;
    
    // Calculate time savings
    const automatedTime = processTime * 0.05;
    const timeSavingPerProcess = processTime - automatedTime;
    const annualTimeSavingHours = (timeSavingPerProcess * annualVolume) / 60;
    
    // Calculate cost savings
    const laborCostSavings = annualTimeSavingHours * hourlyLaborCost;
    
    // Calculate error reduction savings
    const errorReductionRate = errorRateBefore - 1;
    const errorReductionSavings = (errorReductionRate / 100) * annualVolume * hourlyLaborCost;
    
    // Calculate implementation costs
    let implementationCost;
    switch(complexity) {
      case 'simple': implementationCost = 3000; break;
      case 'basic': implementationCost = 4000; break;
      case 'moderate': implementationCost = 5000; break;
      case 'complex': implementationCost = 7500; break;
      case 'veryComplex': implementationCost = 10000; break;
      default: implementationCost = 5000;
    }
    
    const implementationCostWithMargin = implementationCost * 1.4; // Add 40% margin on implementation only
    const licenseCost = rpaType === 'uipath' ? 15000 : 0;
    const totalImplementationCost = implementationCostWithMargin + licenseCost;
    
    let maintenancePercentage;
    switch(complexity) {
      case 'simple': maintenancePercentage = 5; break;
      case 'basic': maintenancePercentage = 7; break;
      case 'moderate': maintenancePercentage = 10; break;
      case 'complex': maintenancePercentage = 12; break;
      case 'veryComplex': maintenancePercentage = 15; break;
      default: maintenancePercentage = 10;
    }
    const annualMaintenance = implementationCostWithMargin * (maintenancePercentage / 100);
    const totalAnnualSavings = laborCostSavings + errorReductionSavings - annualMaintenance;
    const roi = (totalAnnualSavings / totalImplementationCost) * 100;
    const paybackPeriod = (totalImplementationCost / totalAnnualSavings) * 12;
    const fteSavings = annualTimeSavingHours / 2080;
    
    const results = {
      annualVolume,
      annualCostSavings: Math.round(totalAnnualSavings),
      roi: Math.round(roi),
      paybackPeriod: Math.round(paybackPeriod),
      timeSavedAnnually: Math.round(annualTimeSavingHours * 10) / 10,
      fteSavings: Math.round(fteSavings * 10) / 10,
      errorReductionSavings: Math.round(errorReductionSavings),
      totalImplementationCost,
      automatedTime,
      processTime,
      errorRateBefore,
      maintenanceCost: annualMaintenance,
      licensesCost: licenseCost,
    };
    
    const dataToPass = {
      difficultyLevel: complexity,
      frequencyType: frequency,
      currentProcessTimeMinutes: processTime,
      rpaType: rpaType,
      ...results,
    };
    
    onCalculate(dataToPass);
  };

  const getComplexityDescription = () => {
    switch(complexity) {
      case 'simple': return '1-3 steps, straightforward rules';
      case 'basic': return '4-7 steps, clear rules';
      case 'moderate': return 'Multiple steps, standard rules';
      case 'complex': return 'Many steps, some exceptions';
      case 'veryComplex': return 'Advanced integrations, exceptions handling';
      default: return '';
    }
  };

  const getImplementationCost = () => {
    switch(complexity) {
      case 'simple': return '€4,200';
      case 'basic': return '€5,600';
      case 'moderate': return '€7,000';
      case 'complex': return '€10,500';
      case 'veryComplex': return '€14,000';
      default: return '€7,000';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ 
        borderRadius: 4, 
        position: 'relative',
        overflow: 'visible',
        p: 2
      }}>
        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box sx={{ 
            p: 2, 
            mb: 4, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(90, 125, 154, 0.1) 0%, rgba(90, 125, 154, 0.05) 100%)',
            border: '1px solid rgba(90, 125, 154, 0.2)',
            overflow: 'hidden'
          }}>
            <Typography variant="body2" sx={{ color: '#5a7d9a', fontStyle: 'italic', lineHeight: 1.6 }}>
              <strong>Disclaimer:</strong> This calculator provides a cost simulation and ROI estimation. 
              Results are for informational purposes only and do not constitute a binding agreement.
            </Typography>
          </Box>
        </motion.div>

        {/* Hourly Labor Cost */}
        <Section 
          title="Hourly Labor Cost" 
          tooltip="Average hourly cost of employees performing this task"
          delay={0.1}
        >
          <TextField
            fullWidth
            variant="outlined"
            value={hourlyLaborCost}
            onChange={(e) => setHourlyLaborCost(Number(e.target.value) || 0)}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 20px rgba(244, 114, 22, 0.15)',
                },
                '& fieldset': {
                  borderColor: 'rgba(0,0,0,0.1)',
                  borderWidth: 2,
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(244, 114, 22, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#f47216',
                },
              }
            }}
          />
        </Section>

        {/* Current Process Time */}
        <Section 
          title="Current Process Time (Minutes)"
          tooltip="How long does this process take to complete manually?"
          delay={0.15}
        >
          <ModernSlider 
            value={processTime}
            onChange={setProcessTime}
            min={5}
            max={60}
            suffix=" min"
            delay={0.2}
          />
          <Typography variant="body2" sx={{ mt: 2, color: '#8a9aa8' }}>
            Automated time will be <strong style={{ color: '#f47216' }}>5%</strong> of current time 
            (<strong style={{ color: '#db1d2b' }}>{(processTime * 0.05).toFixed(1)} minutes</strong>)
          </Typography>
        </Section>

        {/* Process Frequency */}
        <Section 
          title="Process Frequency"
          tooltip="How often is this process executed?"
          delay={0.2}
        >
          <ToggleButtonGroup 
            options={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
            ]}
            value={frequency}
            onChange={setFrequency}
            delay={0.25}
          />
          
          {frequency === 'daily' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1.5, color: '#5a6775', fontWeight: 500 }}>
                  Number of times per day
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={timesPerDay}
                  onChange={(e) => setTimesPerDay(Number(e.target.value) || 0)}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.1)', borderWidth: 2 },
                      '&:hover fieldset': { borderColor: 'rgba(244, 114, 22, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#f47216' },
                    }
                  }}
                />
              </Box>
            </motion.div>
          )}
          
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            borderRadius: 2, 
            bgcolor: 'rgba(244, 114, 22, 0.05)',
            border: '1px solid rgba(244, 114, 22, 0.1)'
          }}>
            <Typography variant="body2" sx={{ color: '#5a6775' }}>
              📊 Annual Volume: <strong style={{ color: '#f47216' }}>{timesPerDay * workingDaysPerWeek * 52}</strong> processes/year
            </Typography>
          </Box>
        </Section>

        {/* Working Days Per Week */}
        <Section 
          title="Working Days Per Week"
          tooltip="Number of days per week this process runs"
          delay={0.25}
        >
          <ModernSlider 
            value={workingDaysPerWeek}
            onChange={setWorkingDaysPerWeek}
            min={1}
            max={7}
            marks={[1, 2, 3, 4, 5, 6, 7].map(v => ({ value: v, label: v.toString() }))}
            delay={0.3}
          />
        </Section>

        {/* Error Rate Before Automation */}
        <Section 
          title="Error Rate Before Automation (%)"
          tooltip="Percentage of errors in manual processing"
          delay={0.3}
        >
          <ModernSlider 
            value={errorRateBefore}
            onChange={setErrorRateBefore}
            min={0}
            max={30}
            suffix="%"
            delay={0.35}
          />
        </Section>

        {/* Error Rate After Automation */}
        <Section title="Error Rate After Automation" delay={0.35}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
            border: '1px solid rgba(76, 175, 80, 0.2)'
          }}>
            <Typography variant="body1" sx={{ color: '#5a6775', mb: 1 }}>
              Automation will reduce errors to minimum
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
              Target Error Rate: 1%
            </Typography>
          </Box>
        </Section>

        {/* RPA Platform */}
        <Section 
          title="Method of Automation"
          tooltip="Choose between licensed RPA platform or open-source tools"
          delay={0.4}
        >
          <ToggleButtonGroup 
            options={[
              { value: 'uipath', label: 'UiPath' },
              { value: 'open source', label: 'Open Source' },
            ]}
            value={rpaType}
            onChange={setRpaType}
            delay={0.45}
          />
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            borderRadius: 2, 
            bgcolor: rpaType === 'uipath' ? 'rgba(244, 114, 22, 0.05)' : 'rgba(76, 175, 80, 0.05)',
            border: `1px solid ${rpaType === 'uipath' ? 'rgba(244, 114, 22, 0.2)' : 'rgba(76, 175, 80, 0.2)'}`
          }}>
            <Typography variant="body2" sx={{ color: '#5a6775' }}>
              💰 Estimated License Cost: <strong style={{ color: rpaType === 'uipath' ? '#f47216' : '#4caf50' }}>
                €{rpaType === 'uipath' ? '15,000' : '0'}
              </strong>
            </Typography>
          </Box>
        </Section>

        {/* Process Complexity */}
        <Section 
          title="Process Complexity"
          tooltip="Complexity level affects implementation cost and maintenance"
          delay={0.45}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <ComplexityButton 
              value="simple"
              currentValue={complexity}
              onClick={() => setComplexity('simple')}
              label="Simple"
              color="#4caf50"
              delay={0.5}
            />
            <ComplexityButton 
              value="basic"
              currentValue={complexity}
              onClick={() => setComplexity('basic')}
              label="Basic"
              color="#2196f3"
              delay={0.55}
            />
            <ComplexityButton 
              value="moderate"
              currentValue={complexity}
              onClick={() => setComplexity('moderate')}
              label="Moderate"
              color="#ff9800"
              delay={0.6}
            />
            <ComplexityButton 
              value="complex"
              currentValue={complexity}
              onClick={() => setComplexity('complex')}
              label="Complex"
              color="#f44336"
              delay={0.65}
            />
            <ComplexityButton 
              value="veryComplex"
              currentValue={complexity}
              onClick={() => setComplexity('veryComplex')}
              label="Very Complex"
              color="#9c27b0"
              delay={0.7}
            />
          </Box>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.75 }}
          >
            <Box sx={{ 
              mt: 3, 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.01) 100%)',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <Typography variant="body2" sx={{ color: '#5a6775', mb: 2 }}>
                <strong>Description:</strong> {getComplexityDescription()}
              </Typography>
              <Box sx={{ 
                display: 'inline-block',
                px: 2,
                py: 1,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #db1d2b 0%, #f47216 100%)',
              }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'white' }}>
                  Implementation Cost: {getImplementationCost()}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Section>
      </Box>
    </motion.div>
  );
}

export default ROICalculator;
